import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  CriteriaRequest,
  Filter,
  SortHelper,
} from '@daas-consulting/core-cross-interfaces';

export type Filters = Filter[];

@Injectable()
export class QueryStringService {
  readonly #activatedRoute: ActivatedRoute;

  constructor(activatedRoute: ActivatedRoute) {
    this.#activatedRoute = activatedRoute;
  }

  get(): CriteriaRequest {
    const filters = this._getQueryParams();
    const pageIndex = this._getQueryParamPageIndex();
    const pageSize = this._getQueryParamPageSize();
    const sort = this._getQueryParamSort();
    const orderBy = SortHelper.parse(sort);
    const rv: CriteriaRequest = {
      filters,
      pageIndex: pageIndex as number,
      pageSize: pageSize as number,
      orderBy,
    };

    return rv;
  }

  private _getQueryParams(): Filters {
    const queryParamMap = this.#activatedRoute.snapshot.queryParamMap;
    const keys = queryParamMap.keys.filter(
      (x) => !/page[_-]?(?:index|size)|sort/i.test(x)
    );
    const rv = keys.reduce((acc, key) => {
      const param = this._getQueryParam(key);
      param.forEach((x) => {
        acc.push(x);
      });

      return acc;
    }, [] as Filters);

    return rv;
  }

  private _getQueryParam(key: string): Filters {
    const values = this.#activatedRoute.snapshot.queryParamMap.getAll(key);
    const rv = values
      .filter((value) => !!value)
      .map((value) => ({ key, value: this._parseQueryParamValue(value) }));

    return rv;
  }

  private _parseQueryParamValue(value: string): unknown {
    let rv: unknown;
    if (
      /^(?:\d{4})-(?:\d{2})-(?:\d{2})T(?:\d{2}):(?:\d{2}):(?:\d{2}).(?:\d{3})Z$/.test(
        value
      )
    ) {
      rv = new Date(Date.parse(value));
    } else if (/^-?\d+$/.test(value)) {
      rv = parseInt(value);
    } else if (/^-?\d*\.?\d+$/.test(value)) {
      rv = parseFloat(value);
    } else {
      rv = value;
    }

    return rv;
  }

  private _getQueryParamPageIndex(): number | undefined {
    const queryParamMap = this.#activatedRoute.snapshot.queryParamMap;
    const key = queryParamMap.keys.find((x) => /page[_-]?index/i.test(x));
    let aux;
    if (key) {
      aux = queryParamMap.get(key);
    }

    const rv = aux ? parseInt(aux) : undefined;
    return rv;
  }

  private _getQueryParamPageSize(): number | undefined {
    const queryParamMap = this.#activatedRoute.snapshot.queryParamMap;
    const key = queryParamMap.keys.find((x) => /page[_-]?size/i.test(x));
    let aux;
    if (key) {
      aux = queryParamMap.get(key);
    }

    const rv = aux ? parseInt(aux) : undefined;
    return rv;
  }

  private _getQueryParamSort(): string | undefined {
    const queryParamMap = this.#activatedRoute.snapshot.queryParamMap;
    const key = queryParamMap.keys.find((x) => /sort/i.test(x));
    let aux;
    if (key) {
      aux = queryParamMap.get(key);
    }

    const rv = aux ?? undefined;
    return rv;
  }
}
